<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchCode"
              :placeholder="$t('attributes.search') + $t('attributes.code')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.country') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.id }}</p>
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="area_name"
        :visible="!selectedArea"
        :label="$t('attributes.area_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.area_name }}
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.code')"
        sortable
        v-slot="props"
      >
        {{ props.row.code }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="english_name"
        :label="$t('attributes.english_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.english_name }}
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isCreateModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <create-form
        :country="selectedItem"
        :is-sending="sending"
        @send="onSend"
      />
    </b-modal>

    <b-modal
      :active.sync="isUpdateModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <update-form
        :country="selectedItem"
        :is-sending="sending"
        @send="onSend"
      />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AREA_API, COUNTRIES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import CreateForm from './CountryCreateForm'
import UpdateForm from './CountryUpdateForm'

export default {
  name: 'CountryList',
  components: { CreateForm, UpdateForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      searchCode: this.query.code || '',
      searchName: this.query.search || '',
      sortField: this.query.sort || 'id',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      loadingAreas: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      isCreateModalActive: false,
      isUpdateModalActive: false,
      sending: false
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.selectedAreaCode = this.selectedArea && this.selectedArea.code
        }

        await this.loadItems()
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadItems() {
      try {
        const api = `${COUNTRIES_API}`
        const params = {
          area_code: this.selectedAreaCode,
          search_code: this.searchCode,
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('country', e)
      } finally {
        this.loadingItems = false
      }
    },
    updateQuery() {
      const query = {
        code: this.searchCode,
        ac: this.selectedAreaCode,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilter: debounce(function() {
      this.updateQuery()
      this.loadItems()
    }, 500),
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null

      this.updateQuery()
      this.loadItems()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
      this.loadItems()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
      this.loadItems()
    },

    onAdd() {
      this.selectedItem = {
        id: null,
        status: false,
        area_code: this.selectedAreaCode,
        code: null
      }
      this.isCreateModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isUpdateModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(COUNTRIES_API, item)
        this.isCreateModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${COUNTRIES_API}/${item.id}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item
        this.items = items

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
